// 🌿 API Craft Back Office - Create Model Page
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { modelsApi } from '../services/api';
import { usePageTitle, PAGE_TITLES } from '../hooks/usePageTitle';
import { useModels } from '../hooks/useModels';
import Layout from '../components/Layout';
import { ModelField } from '../types';

// interface ModelDefinition {
//   name: string;
//   fields: ModelField[];
// }

const CreateModelPage: React.FC = () => {
  const navigate = useNavigate();
  const { models } = useModels();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [modelName, setModelName] = useState('');
  const [fields, setFields] = useState<ModelField[]>([
    { name: 'name', type: 'string', required: true, max_length: 100 }
  ]);
  
  // Set page title
  usePageTitle(PAGE_TITLES.CREATE_MODEL);

  const fieldTypes = [
    'string', 'integer', 'float', 'boolean', 'date', 'datetime', 'email', 'uuid', 'foreign_key'
  ];

  const addField = () => {
    setFields([...fields, { name: '', type: 'string', required: false }]);
  };

  const removeField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const updateField = (index: number, field: Partial<ModelField>) => {
    const updatedFields = [...fields];
    updatedFields[index] = { ...updatedFields[index], ...field };
    setFields(updatedFields);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const modelDefinition = {
        models: [{
          name: modelName,
          fields: fields
        }],
        create_tables: true
      };

      await modelsApi.createModel(modelDefinition);
      navigate('/models');
    } catch (err: any) {
      console.error('Error creating model:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create model';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-forest-900">Create New Model</h1>
            <p className="text-sage-600 mt-2">Define your data structure and generate API endpoints automatically</p>
          </div>
          <button
            onClick={() => navigate('/models')}
            className="px-4 py-2 text-sage-600 hover:text-forest-700 transition-colors"
          >
            ← Back to Models
          </button>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-soft border border-mint-200 overflow-hidden">
          <div className="p-6 border-b border-mint-200 bg-gradient-to-r from-mint-50 to-sage-50">
            <h2 className="text-xl font-semibold text-forest-800">Model Definition</h2>
            <p className="text-sage-600 text-sm mt-1">Configure your model name and fields</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Model Name */}
            <div>
              <label htmlFor="modelName" className="block text-sm font-medium text-forest-700 mb-2">
                Model Name
              </label>
              <input
                type="text"
                id="modelName"
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                placeholder="e.g., Product, User, Order"
                className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 transition-colors"
                required
              />
              <p className="text-xs text-sage-500 mt-1">Use PascalCase (e.g., ProductCategory)</p>
            </div>

            {/* Fields */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-forest-700">
                  Fields
                </label>
                <button
                  type="button"
                  onClick={addField}
                  className="px-3 py-1 bg-mint-500 text-white rounded-lg hover:bg-mint-600 transition-colors text-sm"
                >
                  + Add Field
                </button>
              </div>

              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div key={index} className="p-4 bg-mint-50 rounded-lg border border-mint-200">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* Field Name */}
                      <div>
                        <label className="block text-xs font-medium text-forest-600 mb-1">Name</label>
                        <input
                          type="text"
                          value={field.name}
                          onChange={(e) => updateField(index, { name: e.target.value })}
                          placeholder="field_name"
                          className="w-full px-3 py-2 border border-sage-300 rounded-lg text-sm focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                          required
                        />
                      </div>

                      {/* Field Type */}
                      <div>
                        <label className="block text-xs font-medium text-forest-600 mb-1">Type</label>
                        <select
                          value={field.type}
                          onChange={(e) => updateField(index, { type: e.target.value })}
                          className="w-full px-3 py-2 border border-sage-300 rounded-lg text-sm focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                        >
                          {fieldTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>

                      {/* Max Length (for strings) */}
                      {field.type === 'string' && (
                        <div>
                          <label className="block text-xs font-medium text-forest-600 mb-1">Max Length</label>
                          <input
                            type="number"
                            value={field.max_length || ''}
                            onChange={(e) => updateField(index, { max_length: parseInt(e.target.value) || undefined })}
                            placeholder="100"
                            className="w-full px-3 py-2 border border-sage-300 rounded-lg text-sm focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                          />
                        </div>
                      )}

                      {/* Foreign Key Reference */}
                      {field.type === 'foreign_key' && (
                        <div>
                          <label className="block text-xs font-medium text-forest-600 mb-1">Reference Model</label>
                          <select
                            value={field.reference_model || ''}
                            onChange={(e) => updateField(index, { reference_model: e.target.value })}
                            className="w-full px-3 py-2 border border-sage-300 rounded-lg text-sm focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                            required
                          >
                            <option value="">Select a model...</option>
                            {models?.filter(model => model.model !== modelName).map(model => (
                              <option key={model.model} value={model.model}>
                                {model.model} ({model.table_structure?.fields?.length || 0} fields)
                              </option>
                            ))}
                          </select>
                          {(!models || models.length === 0) && (
                            <p className="text-xs text-amber-600 mt-1">
                              💡 Create other models first to use as foreign key references
                            </p>
                          )}
                        </div>
                      )}

                      {/* Required checkbox and actions */}
                      <div className="flex items-center justify-between">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateField(index, { required: e.target.checked })}
                            className="w-4 h-4 text-mint-600 bg-sage-100 border-sage-300 rounded focus:ring-mint-500"
                          />
                          <span className="text-xs text-forest-600">Required</span>
                        </label>
                        
                        {fields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeField(index)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-forest-600 mb-1">Description (optional)</label>
                      <input
                        type="text"
                        value={field.description || ''}
                        onChange={(e) => updateField(index, { description: e.target.value })}
                        placeholder="Field description..."
                        className="w-full px-3 py-2 border border-sage-300 rounded-lg text-sm focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-mint-200">
              <button
                type="button"
                onClick={() => navigate('/models')}
                className="px-6 py-3 text-sage-600 hover:text-forest-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !modelName || fields.length === 0}
                className="px-6 py-3 bg-gradient-to-r from-mint-500 to-emerald-600 text-white rounded-lg hover:from-mint-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-soft"
              >
                {isLoading ? 'Creating...' : 'Create Model'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CreateModelPage;
